import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { ErrorBoundary } from '../components/errorboundary';
import MedicarePages from '../pages/medicare';
import ContractManagement from '../pages/contractmanagement';
import ReportSubscriptions from '../pages/reportsubscriptions';
import MedicareAuditQueueSearch from '../pages/medicare/auditqueue/search/search';
import makeAuditSearchService from '../services/auditsearchservice';
import MedicareReviewSearch from '../pages/medicare/review/search/search';
import makeReviewSearchService from '../services/reviewsearchservice';
import MedicareReviewDetails from '../pages/medicare/review/details/components/details';
import MedicareAuditDetails from '../pages/medicare/auditqueue/details/components/details';
import medicareDetailsService from '../services/medicaredetailsservice';
import contractManagementPageService from '../services/contractmanagementpageservice';
import createPopoutCardService from '../services/popoutcardservice';
import PopoutCard from '../components/popoutcard/popoutcard';
import ACAQueueFilter from '../pages/aca/codingqueue/filter/filter';
import makeCodingQueueFilterService from '../services/codingqueuefilterservice';
import ACACodingQueueDetails from '../pages/aca/codingqueue/details/components/details';
import ACAAuditSearch from '../pages/aca/auditqueue/search/search';
import ACAAuditDetails from '../pages/aca/auditqueue/details/components/details';
import ACAReviewSearch from '../pages/aca/review/search/search';
import ACAReviewDetails from '../pages/aca/review/details/components/details';
import makeReportSubscriptionService from '../services/reportsubscriptionservice';
import {
  ClientReviewLandingPage
} from '../pages/clientReview/components/clientReviewLandingPage.jsx';
import {
  WorkDetailPage as ClientLinkedAddWorkDetailPage
} from '../pages/clientReview/components/workDetailPage.jsx';
import {
  createClientReviewLandingPageService
} from '../pages/clientReview/services/landingPageService.js';
import {
  createWorkPageDetailService as createLinkedAddDetailService
} from '../pages/clientReview/services/workPageDetailService.js';

const initializeRoutes = (backendRoutes) => {
  const gotoPopoutAuditQueue = encId => `/Medicare/AuditQueue/Popout/${encId}`;
  const gotoPopoutCodingQueue = encId => `/Medicare/CodingQueue/Popout/${encId}`

  // medicare coding queue details and filter routes / services
  // helper funcs
  const redirectToReviewDetails = encId => `/Medicare/Review/Details/${encId}`;

  const redirectToDetails = lob => encId => `/${lob}/CodingQueue/Details/${encId}`;
  const redirectToMedicareDetails = redirectToDetails('Medicare');

  const redirectToFilter = lob => `/${lob}/CodingQueue/Filter`;
  const redirectToMedicareFilter = redirectToFilter('Medicare');
  const redirectToACAFilter = redirectToFilter('ACA');

  const medicareFilterServiceRoutes = {
    init: backendRoutes.MedicareCodingQueueFilter,
    beginWork: backendRoutes.MedicareCodingQueueFilter,
    redirectToDetails: redirectToMedicareDetails
  };

  const medicareFilterService = makeCodingQueueFilterService(
    medicareFilterServiceRoutes
  );

  const medicareDetailsServiceRoutes = {
    init: backendRoutes.MedicareCodingQueueDetails,
    placeOnHold: backendRoutes.MedicareCoreEncounterHold,
    removeFromHold: backendRoutes.MedicareCoreEncounterHold,
    exitEncounter: backendRoutes.MedicareExitEncounterCodingQueue,
    finalizeEncounter: backendRoutes.MedicareCodingQueueFinalize,
    unfinalizeEncounter: backendRoutes.MedicareCodingQueueUnfinalize,
    addComment: backendRoutes.MedicareCoreEncounterComments,
    addDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    approveDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    unapproveDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    removeDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    unremoveDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    setDxAsUnworked: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    markEncounterAsInvalid: backendRoutes.MedicareCodingQueueInvalidEncounter,
    redirectToDetails: redirectToMedicareDetails,
    redirectToFilter: redirectToMedicareFilter,
    redirectToReviewDetails,
    gotoPopout: gotoPopoutCodingQueue,
    reviewHold: backendRoutes.MedicareCoreEncounterHoldReview
  };

  const medDetailsService = medicareDetailsService(medicareDetailsServiceRoutes);

  // medicare review details routes and service
  const medicareReviewDetailsServiceRoutes = {
    init: backendRoutes.MedicareCoreEncounterReviewDetails,
    goToCodingQueueDetails: encId => `/Medicare/CodingQueue/Details/${encId}`,
    goToAuditQueueDetails: encId => `/Medicare/AuditQueue/Details/${encId}`,
    redirectToReviewDetails
  };

  const medicareReviewDetailsService = medicareDetailsService(
    medicareReviewDetailsServiceRoutes
  );

  // medicare audit routes and service
  const redirectToAuditDetails = lob => encId => `/${lob}/AuditQueue/Details/${encId}`;
  const redirectToMedicareAuditDetails = redirectToAuditDetails('Medicare');

  const redirectToAuditFilter = lob => `/${lob}/AuditQueue/Search`;
  const redirectToMedicareAuditFilter = redirectToAuditFilter('Medicare');
  const redirectToACAAuditFilter = redirectToAuditFilter('ACA');

  const medicareAuditDetailsServiceRoutes = {
    init: backendRoutes.MedicareAuditQueueDetails,
    placeOnHold: backendRoutes.MedicareAuditQueueAuditHold,
    removeFromHold: backendRoutes.MedicareAuditQueueAuditHold,
    exitEncounter: backendRoutes.MedicareExitEncounterAuditQueue,
    addComment: backendRoutes.MedicareCoreEncounterComments,
    addDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    approveDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    unapproveDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    removeDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    unremoveDx: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    setDxAsUnworked: backendRoutes.MedicareCodingQueueEncounterCmsCode,
    completeEncounter: backendRoutes.MedicareAuditQueueCompleteAudit,
    redirectToDetails: redirectToMedicareAuditDetails,
    redirectToFilter: redirectToMedicareAuditFilter,
    redirectToReviewDetails,
    gotoPopout: gotoPopoutAuditQueue,
    reviewHold: backendRoutes.MedicareCoreEncounterHoldReview,
    resolveAuditReason: backendRoutes.MedicareAuditReasonResolution
  };

  const medicareAuditDetailsService = medicareDetailsService(
    medicareAuditDetailsServiceRoutes
  );

  const contractServiceRoutes = {
    init: backendRoutes.CodingQueueContractManagementInit,
    selectContract: backendRoutes.CodingQueueContractManagementContractDetails,
    toggleSetting: backendRoutes.CodingQueueContractManagementToggleSetting,
    getAffectedEncounters:
      backendRoutes.CodingQueueContractManagementAffectedEncounters,
    toggleAuditType: backendRoutes.CodingQueueContractManagementToggleAuditType,
    addItemToAudit:
      backendRoutes.CodingQueueContractManagementAddItemToAuditType,
    removeItemFromAudit:
      backendRoutes.CodingQueueContractManagementRemoveItemFromAuditType,
    removeAuditReasons:
      backendRoutes.CodingQueueContractManagementRemoveAuditReasons,
    manualAutofinalize:
      backendRoutes.CodingQueueContractManagementAutofinalizeEncounters,
    purgeEncounters:
      backendRoutes.CodingQueueContractManagementPurge,
    updateNextRecordStrategySettingController:
      backendRoutes.UpdateNextRecordStrategySetting,
  toggleIsRereadDisabled: backendRoutes.CodingQueueContractManagementIsRereadDisabled
  };

  const contractPageService = contractManagementPageService(contractServiceRoutes);

  const auditSearchServiceRoutes = {
    init: backendRoutes.MedicareAuditQueueFilter,
    getCodersForContract: backendRoutes.MedicareAuditQueueAuditCoders,
    getEncounterCount: backendRoutes.MedicareAuditQueueFilter,
    beginWork: backendRoutes.MedicareAuditQueueNextEncounter,
    redirectToDetails: redirectToMedicareAuditDetails
  };

  const auditSearchService = makeAuditSearchService(auditSearchServiceRoutes);

  const reviewSearchServiceRoutes = {
    init: backendRoutes.MedicareCoreEncounterReviewListing,
    search: backendRoutes.MedicareCoreEncounterReviewListing,
    getCodersForContract: backendRoutes.MedicareCoreEncounterReviewListingCoders,
    redirectToDetails: redirectToReviewDetails
  };

  const reviewSearchService = makeReviewSearchService(reviewSearchServiceRoutes);

  const codingQueuePopoutCardServiceRoutes = {
    init: backendRoutes.MedicareCodingQueuePopout
  };
  const codingQueuePopoutCardService = createPopoutCardService(
    codingQueuePopoutCardServiceRoutes
  );

  const auditQueuePopoutCardServiceRoutes = {
    init: backendRoutes.MedicareAuditQueuePopout
  };
  const auditQueuePopoutCardService = createPopoutCardService(
    auditQueuePopoutCardServiceRoutes
  );

  const acaQueueFilterServiceRoutes = {
    init: backendRoutes.AcaCodingQueueFilter,
    beginWork: backendRoutes.AcaCodingQueueFilter,
    redirectToDetails: encId => `/ACA/CodingQueue/Details/${encId}`
  };
  const acaQueueFilterService = makeCodingQueueFilterService(
    acaQueueFilterServiceRoutes
  );

  const acaCodingQueueDetailServiceRoutes = {
    init: backendRoutes.AcaCodingQueueDetails,
    placeOnHold: backendRoutes.AcaCoreEncounterHold,
    removeFromHold: backendRoutes.AcaCoreEncounterHold,
    exitEncounter: backendRoutes.AcaExitEncounterCodingQueue,
    finalizeEncounter: backendRoutes.AcaCodingQueueFinalize,
    unfinalizeEncounter: backendRoutes.AcaCodingQueueUnfinalize,
    addComment: backendRoutes.AcaCoreEncounterComments,
    addDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    approveDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    unapproveDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    removeDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    unremoveDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    setDxAsUnworked: backendRoutes.AcaCodingQueueEncounterCmsCode,
    markEncounterAsInvalid: backendRoutes.AcaCodingQueueInvalidEncounter,
    redirectToDetails: encId => `/ACA/CodingQueue/Details/${encId}`,
    redirectToFilter: redirectToACAFilter,
    redirectToReviewDetails: encId => `/ACA/Review/Details/${encId}`,
    gotoPopout: encId => `/ACA/CodingQueue/Popout/${encId}`,
    reviewHold: backendRoutes.AcaCoreEncounterHoldReview
  };
  const acaCodingQueueDetailService = medicareDetailsService(
    acaCodingQueueDetailServiceRoutes
  );

  const acaAuditQueueSearchServiceRoutes = {
    init: backendRoutes.AcaAuditQueueFilter,
    getCodersForContract: backendRoutes.AcaAuditQueueAuditCoders,
    getEncounterCount: backendRoutes.AcaAuditQueueFilter,
    beginWork: backendRoutes.AcaAuditQueueNextEncounter,
    redirectToDetails: encId => `/ACA/AuditQueue/Details/${encId}`
  };
  const acaAuditQueueSearchService = makeAuditSearchService(
    acaAuditQueueSearchServiceRoutes
  );

  const acaAuditDetailsServiceRoutes = {
    init: backendRoutes.AcaAuditQueueDetails,
    placeOnHold: backendRoutes.AcaAuditQueueAuditHold,
    removeFromHold: backendRoutes.AcaAuditQueueAuditHold,
    exitEncounter: backendRoutes.AcaExitEncounterAuditQueue,
    addComment: backendRoutes.AcaCoreEncounterComments,
    addDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    approveDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    unapproveDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    removeDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    unremoveDx: backendRoutes.AcaCodingQueueEncounterCmsCode,
    setDxAsUnworked: backendRoutes.AcaCodingQueueEncounterCmsCode,
    completeEncounter: backendRoutes.AcaAuditQueueComplete,
    redirectToDetails: encId => `/ACA/AuditQueue/Details/${encId}`,
    redirectToFilter: redirectToACAAuditFilter,
    redirectToReviewDetails: encId => `/ACA/AuditQueue/Details/${encId}`,
    gotoPopout: encId => `/ACA/AuditQueue/Popout/${encId}`,
    reviewHold: backendRoutes.AcaCoreEncounterHoldReview,
    resolveAuditReason: backendRoutes.AcaAuditReasonResolution
  };
  const acaAuditQueueDetailService = medicareDetailsService(
    acaAuditDetailsServiceRoutes
  );

  const acaReviewSearchServiceRoutes = {
    init: backendRoutes.AcaCoreEncounterReviewListing,
    search: backendRoutes.AcaCoreEncounterReviewListing,
    getCodersForContract: backendRoutes.AcaCoreEncounterReviewListingCoders,
    redirectToDetails: encId => `/ACA/Review/Details/${encId}`
  };
  const acaReviewSearchService = makeReviewSearchService(
    acaReviewSearchServiceRoutes
  );

  const acaReviewDetailsServiceRoutes = {
    init: backendRoutes.AcaCoreEncounterReviewDetails,
    goToCodingQueueDetails: encId => `/ACA/CodingQueue/Details/${encId}`,
    goToAuditQueueDetails: encId => `/ACA/AuditQueue/Details/${encId}`,
    redirectToReviewDetails: encId => `/ACA/Review/Details/${encId}`
  };
  const acaReviewDetailsService = medicareDetailsService(
    acaReviewDetailsServiceRoutes
  );

  const acaCodingQueuePopoutServiceRoutes = {
    init: backendRoutes.AcaCodingQueuePopout
  };
  const acaCodingQueuePopoutService = createPopoutCardService(
    acaCodingQueuePopoutServiceRoutes
  );

  const acaAuditQueuePopoutServiceRoutes = {
    init: backendRoutes.AcaAuditQueuePopout
  };
  const acaAuditQueuePopoutService = createPopoutCardService(
    acaAuditQueuePopoutServiceRoutes
  );

  const reportSubscriptionRoutes = {
    route: backendRoutes.MedicareRamReportSubscriptions
  };

  const reportSubscriptionService = makeReportSubscriptionService(
    reportSubscriptionRoutes
  );

  const clientReviewLandingPageServiceRoutes = {
    initializePage: backendRoutes.MedicareRamLinkedAddClientReviewLandingPage,
    beginWork: backendRoutes.MedicareRamLinkedAddClientReviewLandingPage,
    acceptAllReviews: backendRoutes.MedicareRamLinkedAddClientReviewAcceptAllEncounterReviews,
    searchReviews: backendRoutes.MedicareRamLinkedAddClientReviewEncounterReviewSearch
  };

  const clientReviewLandingPageService = createClientReviewLandingPageService(
    clientReviewLandingPageServiceRoutes
  );

  const clientReviewLinkedAddDetailService = createLinkedAddDetailService({
    initializePage: backendRoutes.MedicareRamLinkedAddClientReviewWorkPage,
    acceptEncounterLineItem: backendRoutes
      .MedicareRamLinkedAddClientReviewEncounterLineItem,
    rejectEncounterLineItem: backendRoutes.
    MedicareRamLinkedAddClientReviewEncounterLineItem,
    completeEncounterReview: backendRoutes
      .MedicareRamLinkedAddClientReviewEncounter,
    addComment: backendRoutes.MedicareCoreEncounterComments
  });

  const getClientRoutes = (hideNav) => {
    const clientRoutes = (
      <Switch>
        <Route
          path="/ACA/CodingQueue/Filter"
          exact
          component={props =>
            <ErrorBoundary>
              <ACAQueueFilter
                service={acaQueueFilterService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/CodingQueue/Details/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <ACACodingQueueDetails
                service={acaCodingQueueDetailService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/AuditQueue/Search"
          exact
          component={props =>
            <ErrorBoundary>
              <ACAAuditSearch
                service={acaAuditQueueSearchService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/AuditQueue/Details/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <ACAAuditDetails
                service={acaAuditQueueDetailService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/Review/Search"
          exact
          component={props =>
            <ErrorBoundary>
              <ACAReviewSearch
                service={acaReviewSearchService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/Review/Details/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <ACAReviewDetails
                service={acaReviewDetailsService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/CodingQueue/Popout/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <PopoutCard
                service={acaCodingQueuePopoutService}
                hideNav={hideNav}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ACA/AuditQueue/Popout/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <PopoutCard
                service={acaAuditQueuePopoutService}
                hideNav={hideNav}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/CodingQueue/Popout/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <PopoutCard
                service={codingQueuePopoutCardService}
                hideNav={hideNav}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/AuditQueue/Popout/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <PopoutCard
                service={auditQueuePopoutCardService}
                hideNav={hideNav}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/CodingQueue/Filter"
          exact
          component={props =>
            <ErrorBoundary>
              <MedicarePages.codingQueue.filter.component
                service={medicareFilterService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/CodingQueue/Details/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <MedicarePages.codingQueue.details.component
                service={medDetailsService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/AuditQueue/Search"
          exact
          component={props =>
            <ErrorBoundary>
              <MedicareAuditQueueSearch
                service={auditSearchService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/AuditQueue/Details/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <MedicareAuditDetails
                service={medicareAuditDetailsService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/Review/Search"
          exact
          component={props =>
            <ErrorBoundary>
              <MedicareReviewSearch
                service={reviewSearchService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/Review/Details/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <MedicareReviewDetails
                service={medicareReviewDetailsService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ContractManagement"
          exact
          component={props =>
            <ErrorBoundary>
              <ContractManagement.component
                service={contractPageService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/ReportSubscriptions"
          exact
          component={props =>
            <ErrorBoundary>
                <ReportSubscriptions.component
                service={reportSubscriptionService}
                  {...props}
                />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/ClientReview/Landing"
          exact
          component={props =>
            <ErrorBoundary>
              <ClientReviewLandingPage
                service={clientReviewLandingPageService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        <Route
          path="/Medicare/ClientReview/Work/Detail/:encounterId"
          exact
          component={props =>
            <ErrorBoundary>
              <ClientLinkedAddWorkDetailPage
                service={clientReviewLinkedAddDetailService}
                {...props}
              />
            </ErrorBoundary>
          }
        />

        {/* TODO fancy 404 page */}
        <Route
          component={({ location }) => (
            <h1>Nothing found for {location.pathname}</h1>
          )}
        />
      </Switch>
    );

    return clientRoutes;
  };

  return getClientRoutes;
};

export default initializeRoutes;
// export default getClientRoutes;
